import { useState } from 'react';
import { KoobAccordion, KoobTooltip } from '@koob/ui';
import BookingExtrasModal from './BookingExtrasModal';
import { BookingStatusBadge } from '@koob/booking';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils';
import { isEmpty } from 'lodash';
import { getExtraTitle, sortExtras } from '@koob/utils';
import { AdminBookingStateEnum } from '@koob/enums';
import { formatDate, yearsSince } from '@koob/dates';
import ExperienceExtraComposition from 'containers/Experiences/Partials/ExperienceExtraComposition';

const getExtraPaxCount = extra =>
  (extra?.adultPax || 0) + (extra?.childrenBirthdates?.length || 0);

// KD-3650 — the travellers of the occurrence a sub-option hangs from.
//
// A sub-option takes no assignment of its own: it is covered by the guests of
// the service it was ticked under, and the booking carries no link back to a
// given occurrence of that service — `parentId` names the parent EXPERIENCE, so
// it is true of every occurrence at once, and listing them all showed a
// sub-option of the four-seat leg with the whole party.
//
// A sub-option is priced on the composition of its own occurrence, so the pax
// count identifies it whenever it is unambiguous. Two occurrences booked for
// the same number of travellers stay indistinguishable — nothing in the booking
// tells them apart — and fall back on the union, as before.
const getParentTravelers = (extras, extra) => {
  const parents = extras.filter(
    e =>
      Number(e.id) === Number(extra.parentId) && e.dayIndex === extra.dayIndex,
  );

  const sameComposition = parents.filter(
    e => getExtraPaxCount(e) === getExtraPaxCount(extra),
  );

  const matched = sameComposition.length === 1 ? sameComposition : parents;

  return matched.flatMap(e => e.travelers ?? []);
};

const BookedExtras = ({ extras, currency, reload, bookingId }) => {
  const { t } = useTranslation('experiences');
  const [selectedOption, setSelectedOption] = useState(null);

  const sortedExtras = sortExtras(extras);

  const extrasGroup = new Set();

  return (
    <div className="flex flex-col space-y-3 rounded-lg w-full min-w-0 bg-gray-100 p-4">
      {sortedExtras?.map((extra, index) => {
        const extraTitle = getExtraTitle({
          extra,
          extrasGroup,
          translation: t,
        });

        const extraState = extra?.state;
        let travelers = extra?.travelers || [];

        if (extra?.isIncluded) travelers = [];
        if (extra.parentId) {
          travelers = getParentTravelers(extras, extra);
        }

        return (
          <div
            // KD-3650 — one row per occurrence of a service, and they share
            // everything the row is named after: only the composition line the
            // booking stores them under is unique.
            key={extra.extraCompositionId ?? index}
            className="w-full"
            data-testid={`day-${extra.dayIndex + 1}`}
          >
            {extraTitle}
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div
                onClick={() => setSelectedOption(extra)}
                className={`font-medium text-gray-500 text-sm min-w-0 truncate ${
                  extraState === AdminBookingStateEnum.CANCELLED
                    ? 'line-through'
                    : 'underline'
                }`}
                title={extra.name}
                data-testid={`primary-option-day-${extra.dayIndex + 1}-${extra.name
                  ?.split(' ')
                  .join('-')}`}
              >
                {extra.name}
              </div>
              {/* KD-3650 — two occurrences of a service are two rows bearing
                  the same name: the composition each one was booked for is
                  what tells them apart. */}
              {getExtraPaxCount(extra) > 0 && (
                <div className="flex-shrink-0">
                  <ExperienceExtraComposition
                    adults={extra.adultPax}
                    childrenCount={extra.childrenBirthdates?.length}
                  />
                </div>
              )}
              {extraState === AdminBookingStateEnum.CANCELLED ? (
                <div className="flex-shrink-0 whitespace-nowrap">
                  <BookingStatusBadge status={extraState} />
                </div>
              ) : (
                extra?.price !== 0 && (
                  <div className="font-medium text-orange-500 flex-shrink-0">
                    <div className="flex justify-end gap-x-2 items-center">
                      <div>
                        {formatCurrency({
                          amount: extra.price,
                          currency: extra?.currency ?? currency,
                        })}
                      </div>
                      {extra?.price !== extra?.priceWithoutPromo && (
                        <div className="text-xs line-through text-gray-500">
                          {formatCurrency({
                            amount: extra?.priceWithoutPromo,
                            currency: extra?.currency ?? currency,
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
            {!isEmpty(travelers) && (
              <div className="mt-1 pl-2 border-l-2 border-gray-300 space-y-0.5">
                {travelers.map((traveler, index) => {
                  const isTravelerChild =
                    !!traveler?.birthdate &&
                    yearsSince(traveler.birthdate) < 18;

                  const travelerIcon = (
                    <i
                      className={`fa-regular ${
                        isTravelerChild ? 'fa-child' : 'fa-user'
                      } text-xs`}
                    />
                  );
                  return (
                    <div
                      key={index}
                      className="text-xs text-gray-600 flex items-center space-x-1 gap-1"
                    >
                      {travelerIcon}
                      <span>
                        {traveler.firstName} {traveler.lastName}
                      </span>
                      {traveler.birthdate && (
                        <span className="text-gray-600">
                          {(() => {
                            const label = formatDate(
                              traveler.birthdate,
                              'DD/MM/YYYY',
                            );
                            return label ? `(${label})` : null;
                          })()}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {selectedOption && (
        <BookingExtrasModal
          extra={selectedOption}
          open={!!selectedOption}
          setOpen={() => setSelectedOption(null)}
          currency={currency}
          reload={reload}
          extras={sortedExtras}
          bookingId={bookingId}
        />
      )}
    </div>
  );
};

const BookingExtras = ({ booking, reload }) => {
  const { t } = useTranslation('booking', { keyPrefix: 'summary' });
  const hasOptions = booking?.experiencesBooked?.some(
    experience => experience?.extras.length > 0,
  );

  if (isEmpty(booking?.experiencesBooked) || !hasOptions) {
    return null;
  }

  const extras = booking?.experiencesBooked[0]?.extras ?? [];
  const cancelledExtras = extras.filter(
    e => e?.state === AdminBookingStateEnum.CANCELLED,
  );
  const allCancelled = cancelledExtras.length === extras.length;

  return (
    <div className="w-full min-w-0">
      {cancelledExtras.length > 0 && (
        <div className="mb-3 font-medium">
          {allCancelled
            ? t('cancelledExtras', { count: cancelledExtras.length })
            : t('someCancelledExtras', {
                count: cancelledExtras.length,
                cancelled: cancelledExtras.length,
                total: extras.length,
              })}
        </div>
      )}
      <KoobAccordion
        title={
          <div
            className="flex items-center space-x-3"
            data-testid="selectedOptions"
          >
            <span>{t('extrasTitle')}</span>
            <KoobTooltip
              tip={t('extrasInfo')}
              posKoobModalition="top"
              hasArrow={false}
              color="purple"
            >
              <i className="fa-regular fa-circle-info" />
            </KoobTooltip>
          </div>
        }
        alwaysOpen
      >
        <BookedExtras
          extras={extras}
          currency={booking?.currency}
          reload={reload}
          bookingId={booking?.id}
        />
      </KoobAccordion>
    </div>
  );
};

export default BookingExtras;
